import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ChangeDetectionStrategy} from '@angular/core';
import {
  IE3AddressBook,
  IE3CL707,
  IE3Communication,
  IE3FilingType,
  IE3Party,
  IE3UpsertPartyInAddressBook
} from "@portbase/bezoekschip-service-typescriptmodels";
import {PortvisitUtils} from "../../../../../refdata/portvisit-utils";
import {ConsignmentUtils} from "../../../consignment.utils";
import {cloneDeep, isEmpty} from "lodash";
import {checkValidity, clearValidation, sendCommand, uuid} from "../../../../../common/utils";
import {AppContext} from "../../../../../app-context";
import {Observable} from "rxjs";
import countryValidations from "./country-validation.json";
import {Handler} from "../../../../../common/handlers/handler";
import {View} from "../../../../../common/handlers/view";
import {HandleEvent} from "../../../../../common/handlers/handle-event";
import {ErrorWithPointers} from "../../../../../common/errors/errors";
import {HandleQuery} from "../../../../../common/handlers/handle-query";
import {map} from "rxjs/operators";
import {ConsignmentRules} from "../../../consignment.rules";

@Component({
    selector: 'app-consignment-party-details',
    templateUrl: './consignment-party-details.component.html',
    styleUrls: ['./consignment-party-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Handler()
export class ConsignmentPartyDetailsComponent extends View implements OnInit {
  portVisitUtils = PortvisitUtils;
  utils = ConsignmentUtils;
  rules = ConsignmentRules;
  validations: CountryValidation[] = countryValidations as CountryValidation[];

  @Input() cargoDeclarantId: string;
  @Input() label: string;
  @Input() party: IE3Party;
  @Input() editMode: boolean;
  @Input() required: boolean;
  @Input() editAllowed: boolean = true;
  @Input() customAddTemplate: TemplateRef<any>;
  @Input() deletionAllowed: boolean;
  @Input() communicationRequired: boolean;
  @Input() tooltip: string;
  @Input() showTypeOfPerson: boolean = true;
  @Input() showCommunications: boolean = true;
  // Passed by the master- and house-level consignment modals; undefined keeps the default (ENS-strict) address and
  // telephone rules.
  @Input() filingType: IE3FilingType;

  addressBookId: string;
  partyEditMode: boolean;
  backupParty: IE3Party;
  addressBook: IE3AddressBook;
  countryValidation: CountryValidation;
  defaultTelephone: IE3Communication = { type: IE3CL707.TE };
  defaultEmail: IE3Communication = { type: IE3CL707.EM };
  errors: ErrorWithPointers[];

  @Output() partyChange: EventEmitter<IE3Party> = new EventEmitter<IE3Party>();

  ngOnInit() {
    if (this.party) {
      if (this.party?.name || this.party?.identificationNumber) {
        ConsignmentUtils.findParties(null, this.cargoDeclarantId, this.party)
          .subscribe((p: IE3AddressBook[]) => this.updateAddressBook(p[0]));
      }
      this.updateCountryValidation();
      if (this.party["isNewRecord"]) {
        this.onPartyChange(this.newParty(this.party.name));
      }
    }
  }

  @HandleEvent("validationFailed", { global: true })
  validationFailed() {
    if (!checkValidity(this.elementRef)) {
      this.partyEditMode = true;
    }
  }

  findParties = (term: string): Observable<IE3AddressBook[]> => ConsignmentUtils.findParties(term, this.cargoDeclarantId);

  get streetRequired() {
    return this.rules.isPartyAddressDetailRequired(this.filingType) && !this.party.address.poBox;
  }

  get poBoxRequired() {
    return this.rules.isPartyAddressDetailRequired(this.filingType)
      && (!this.party.address.street || !this.party.address.number);
  }

  get postCodeRequired() {
    return this.rules.isPartyAddressDetailRequired(this.filingType)
      && ['POSTAL_CODE', 'POSTAL_CODE_AND_STREET'].includes(this.countryValidation?.validationCode);
  }

  get isTsdFiling() {
    return this.rules.isTemporaryStorageOnlyConsignment(this.filingType);
  }

  get telephone() {
    return this.party.communications.find(c => c.type === IE3CL707.TE) || this.defaultTelephone;
  }

  get email() {
    return this.party.communications.find(c => c.type === IE3CL707.EM) || this.defaultEmail;
  }

  toggleEditMode = () => {
    this.partyEditMode = !this.partyEditMode;
    if (this.partyEditMode) {
      this.backupParty = cloneDeep(this.party);
    }
  }

  cancel = () => {
    this.party = this.backupParty;
    this.toggleEditMode();
    clearValidation(this.elementRef);
  }

  save = () => {
    if (checkValidity(this.elementRef)) {
      this.processModel();
      this.toggleEditMode();
      this.partyChange.emit(this.party);
      if (this.addressBook["saveInAddressBook"]) {
        this.addressBookId = this.addressBookId || uuid();
        sendCommand("com.portbase.bezoekschip.common.api.consignments.addressbook.UpsertPartyInAddressBook", <IE3UpsertPartyInAddressBook> {
          addressBookId: this.addressBookId,
          party: this.party
        }, () => AppContext.registerSuccess("Successfully saved party in address book"));
      }
    }
  }

  processModel = () => {
    if (!this.party.communications?.find(c => c.type === IE3CL707.TE) && this.defaultTelephone.identifier) {
      this.party.communications.push(this.defaultTelephone);
    }
    if (!this.party.communications?.find(c => c.type === IE3CL707.EM) && this.defaultEmail.identifier) {
      this.party.communications.push(this.defaultEmail);
    }
    this.party.communications = this.party.communications?.filter(c => c.identifier) || [];
  }

  newParty = (value: string): IE3AddressBook => {
    const addressBook = this.createAddressBookFromParty({
      name: value,
      address: {},
      communications: []
    });
    addressBook["isNewRecord"] = true;
    return addressBook;
  }

  onPartyChange = (value: IE3AddressBook) => {
    this.addressBook = value;
    this.party = value?.party;
    if (value && value["isNewRecord"]) {
      this.partyEditMode = true;
    }
    if (!this.partyEditMode) {
      this.partyChange.emit(this.party);
    }
  }

  private updateAddressBook(addressBook: IE3AddressBook) {
    this.addressBook = addressBook || this.createAddressBookFromParty(this.party);
    this.addressBookId = this.addressBook.addressBookId;
    if (addressBook) {
      this.addressBook["saveInAddressBook"] = true;
    }
  }

  private createAddressBookFromParty = (party: IE3Party): IE3AddressBook => ({
    party: party,
    addressBookId: uuid(),
    ownerIamConnectedId: this.cargoDeclarantId
  });

  onCountryChange(country: string) {
    if (country) {
      this.updateCountryValidation();
    }
  }

  updateCountryValidation = () => {
    this.countryValidation = this.party.address.country
      ? this.validations.find(c => c.countryCode === this.party.address.country) : null;
  }

  deleteParty = () => this.partyChange.emit(null);

  @HandleQuery("getErrors")
  getErrors(): Observable<ErrorWithPointers[]> {
    return this.sendQuery("getConsignmentErrors").pipe(map((e: ErrorWithPointers[]) => {
      const predicate = p => p.model === this.party;
      return this.errors = e.filter(e => e.pointers.some(predicate))
        .map(e => ({
          ...e,
          pointers: e.pointers.filter(predicate)
        }));
    }));
  }

  protected readonly isEmpty = isEmpty;
}

interface CountryValidation {
  countryCode: string;
  validationCode: "POSTAL_CODE_AND_STREET" | "POSTAL_CODE" | "COUNTRY_ONLY";
}
